import { useState, useEffect } from 'react';
import {
  capabilityDa1Codes,
  capabilityDiscoveryUi as capUi,
  capabilityFeatures as FEATURES,
  capabilityTermProfiles as TERM_CAPABILITIES,
} from '../content/demos/capabilityDiscoveryDemo';
import { TerminalWindow } from './TerminalWindow';
import { SubsectionLabel } from './shared/SubsectionLabel';

type QueryPhase = 'idle' | 'sending' | 'responding' | 'done';

export function CapabilityDiscoveryDemo() {
  const [selectedTerm, setSelectedTerm] = useState('xterm-256color');
  const [queryPhase, setQueryPhase] = useState<QueryPhase>('idle');
  const [enabledFeatures, setEnabledFeatures] = useState<Set<string>>(
    new Set()
  );

  const capabilities = TERM_CAPABILITIES[selectedTerm]!;

  const runQuery = () => {
    if (queryPhase !== 'idle' && queryPhase !== 'done') return;
    setQueryPhase('sending');
  };

  useEffect(() => {
    if (queryPhase === 'sending') {
      const timer = setTimeout(() => setQueryPhase('responding'), 800);
      return () => clearTimeout(timer);
    }
    if (queryPhase === 'responding') {
      const timer = setTimeout(() => setQueryPhase('done'), 800);
      return () => clearTimeout(timer);
    }
  }, [queryPhase]);

  const toggleFeature = (featureId: string) => {
    setEnabledFeatures((prev) => {
      const next = new Set(prev);
      if (next.has(featureId)) {
        next.delete(featureId);
      } else {
        next.add(featureId);
      }
      return next;
    });
  };

  return (
    <div className="space-y-10">
      {/* Section 1: TERM Variable */}
      <div className="space-y-4">
        <SubsectionLabel>{capUi.termSubsection}</SubsectionLabel>

        <TerminalWindow>
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="flex items-center gap-2 font-mono text-sm">
                <span className="text-terminal-dim">$</span>
                <span className="text-terminal-cyan">TERM</span>
                <span className="text-terminal-dim">=</span>
                <select
                  value={selectedTerm}
                  onChange={(e) => setSelectedTerm(e.target.value)}
                  className="bg-terminal-bg border border-terminal-border text-terminal-yellow px-2 py-1 text-sm font-mono cursor-pointer hover:border-terminal-dim focus:outline-none focus:border-terminal-fg"
                >
                  {Object.keys(TERM_CAPABILITIES).map((term) => (
                    <option key={term} value={term}>
                      {term}
                    </option>
                  ))}
                </select>
              </div>
              <span className="text-terminal-muted text-sm">
                {capabilities.description}
              </span>
            </div>

            {/* Capability Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-2">
              <CapabilityItem
                label={capUi.capabilityLabels.colors}
                value={
                  capabilities.colors === 0
                    ? capUi.capabilityLabels.none
                    : capabilities.colors.toString()
                }
                supported={capabilities.colors > 0}
              />
              <CapabilityItem
                label={capUi.capabilityLabels.mouse}
                value={
                  capabilities.mouse
                    ? capUi.capabilityLabels.yes
                    : capUi.capabilityLabels.no
                }
                supported={capabilities.mouse}
              />
              <CapabilityItem
                label={capUi.capabilityLabels.altScreen}
                value={
                  capabilities.altScreen
                    ? capUi.capabilityLabels.yes
                    : capUi.capabilityLabels.no
                }
                supported={capabilities.altScreen}
              />
              <CapabilityItem
                label={capUi.capabilityLabels.unicode}
                value={
                  capabilities.unicode
                    ? capUi.capabilityLabels.yes
                    : capUi.capabilityLabels.no
                }
                supported={capabilities.unicode}
              />
            </div>
          </div>
        </TerminalWindow>

        <p className="text-terminal-muted text-sm">
          {capUi.termExplanationBefore}
          <code className="text-terminal-cyan">{capUi.termVarName}</code>
          {capUi.termExplanationAfter}
        </p>
      </div>

      {/* Section 2: Query/Response */}
      <div className="space-y-4">
        <SubsectionLabel>{capUi.querySubsection}</SubsectionLabel>

        <TerminalWindow>
          <div className="space-y-4">
            {/* Query visualization */}
            <div className="flex items-center justify-between gap-4 py-2">
              <div className="text-center flex-1">
                <div className="text-xs text-terminal-dim uppercase mb-2">
                  {capUi.programLabel}
                </div>
                <div className="w-12 h-12 mx-auto border border-terminal-border flex items-center justify-center text-terminal-muted">
                  vim
                </div>
              </div>

              <div className="flex-1 relative h-16">
                {/* Query arrow */}
                <div
                  className={`absolute top-2 left-0 right-0 flex items-center transition-opacity duration-300 ${
                    queryPhase === 'sending' ? 'opacity-100' : 'opacity-30'
                  }`}
                >
                  <div className="flex-1 border-t border-terminal-green border-dashed" />
                  <div className="text-terminal-green px-2 text-xs font-mono whitespace-nowrap">
                    {capUi.queryEscOutgoing}
                  </div>
                  <div className="text-terminal-green">→</div>
                </div>

                {/* Response arrow */}
                <div
                  className={`absolute bottom-2 left-0 right-0 flex items-center transition-opacity duration-300 ${
                    queryPhase === 'responding' || queryPhase === 'done'
                      ? 'opacity-100'
                      : 'opacity-30'
                  }`}
                >
                  <div className="text-terminal-yellow">←</div>
                  <div className="text-terminal-yellow px-2 text-xs font-mono whitespace-nowrap">
                    {capUi.queryEscResponse}
                  </div>
                  <div className="flex-1 border-t border-terminal-yellow border-dashed" />
                </div>
              </div>

              <div className="text-center flex-1">
                <div className="text-xs text-terminal-dim uppercase mb-2">
                  {capUi.terminalLabel}
                </div>
                <div className="w-12 h-12 mx-auto border border-terminal-border flex items-center justify-center text-terminal-muted">
                  tty
                </div>
              </div>
            </div>

            {/* Response decoder */}
            {(queryPhase === 'responding' || queryPhase === 'done') && (
              <div className="bg-terminal-bg/50 border border-terminal-border p-3 space-y-2">
                <div className="text-xs text-terminal-dim uppercase">
                  {capUi.responseDecoded}
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="flex items-center gap-2">
                    <span className="text-terminal-yellow font-mono">64</span>
                    <span className="text-terminal-muted">VT420</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-terminal-yellow font-mono">1</span>
                    <span className="text-terminal-muted">
                      {capabilityDa1Codes['1']}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-terminal-yellow font-mono">4</span>
                    <span className="text-terminal-muted">
                      {capabilityDa1Codes['4']}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-terminal-yellow font-mono">22</span>
                    <span className="text-terminal-muted">
                      {capabilityDa1Codes['22']}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Query button */}
            <div className="flex justify-center">
              <button
                onClick={runQuery}
                disabled={queryPhase === 'sending' || queryPhase === 'responding'}
                className={`px-4 py-2 text-sm font-medium transition-all ${
                  queryPhase === 'sending' || queryPhase === 'responding'
                    ? 'bg-terminal-dim text-terminal-bg cursor-not-allowed'
                    : 'bg-terminal-fg text-terminal-bg hover:bg-terminal-bright-white'
                }`}
              >
                {queryPhase === 'sending'
                  ? capUi.querySending
                  : queryPhase === 'responding'
                    ? capUi.queryReceiving
                    : queryPhase === 'done'
                      ? capUi.querySendAgain
                      : capUi.queryIdle}
              </button>
            </div>
          </div>
        </TerminalWindow>

        <p className="text-terminal-muted text-sm">
          {capUi.queryExplanationBefore}
          <code className="text-terminal-cyan">{capUi.queryDa1Code}</code>
          {capUi.queryExplanationAfter}
        </p>
      </div>

      {/* Section 3: Feature Toggles */}
      <div className="space-y-4">
        <SubsectionLabel>{capUi.featuresSubsection}</SubsectionLabel>

        <TerminalWindow>
          <div className="space-y-3">
            {FEATURES.map((feature) => {
              const isEnabled = enabledFeatures.has(feature.id);
              return (
                <div
                  key={feature.id}
                  className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 py-2 border-b border-terminal-border/50 last:border-0"
                >
                  <label className="flex items-center gap-3 cursor-pointer flex-shrink-0">
                    <input
                      type="checkbox"
                      checked={isEnabled}
                      onChange={() => toggleFeature(feature.id)}
                      className="w-4 h-4 accent-terminal-fg"
                    />
                    <span
                      className={`font-medium text-sm ${isEnabled ? 'text-terminal-fg' : 'text-terminal-muted'}`}
                    >
                      {feature.name}
                    </span>
                  </label>

                  <code
                    className={`text-xs font-mono px-2 py-1 ${
                      isEnabled
                        ? 'text-terminal-green bg-terminal-green/10'
                        : 'text-terminal-dim bg-terminal-bg/50'
                    }`}
                  >
                    {isEnabled ? feature.enable : feature.disable}
                  </code>

                  <span className="text-terminal-dim text-xs sm:ml-auto">
                    {feature.description}
                  </span>
                </div>
              );
            })}
          </div>
        </TerminalWindow>

        <p className="text-terminal-muted text-sm">
          {capUi.featuresFooterBefore}
          <code className="text-terminal-cyan">
            {capUi.featuresFooterEnableSeq}
          </code>
          {capUi.featuresFooterMid}
          <code className="text-terminal-cyan">
            {capUi.featuresFooterDisableSeq}
          </code>
          {capUi.featuresFooterAfter}
        </p>
      </div>
    </div>
  );
}

function CapabilityItem({
  label,
  value,
  supported,
}: {
  label: string;
  value: string;
  supported: boolean;
}) {
  return (
    <div
      className={`px-3 py-2 border ${supported ? 'border-terminal-green/50 bg-terminal-green/5' : 'border-terminal-border bg-terminal-bg/50'}`}
    >
      <div className="text-xs text-terminal-dim uppercase">{label}</div>
      <div
        className={`text-sm font-medium ${supported ? 'text-terminal-green' : 'text-terminal-dim'}`}
      >
        {value}
      </div>
    </div>
  );
}
